/**
 * The setup tracker, compressed to one strip for the dashboard.
 *
 * Same stages, same derivation and same source of truth as /welcome — this
 * reads `useSetupStatus` and `nextSetupAction` rather than re-deciding anything,
 * so the two screens can never disagree about where a client is.
 *
 * It exists only while setup is unfinished, and "finished" means the site is
 * serving on a domain the client owns. Once that is true the strip disappears
 * and the dashboard is just a dashboard.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  SETUP_STAGES,
  STAGE_DEFS,
  isRenderableSetupStatus,
  isSetupComplete,
  nextSetupAction,
  stageIndex,
} from '../../lib/setupFlow';
import { useSetupStatus } from '../../hooks/useSetupStatus';
import { Eyebrow } from '../ui/kit';

export default function SetupProgressStrip() {
  const { status } = useSetupStatus();

  // No usable payload, or nothing left to do — say nothing.
  if (!isRenderableSetupStatus(status) || isSetupComplete(status)) return null;

  const current = stageIndex(status.stage);
  const def = STAGE_DEFS[status.stage];
  const next = nextSetupAction(status);
  const fill = current / (SETUP_STAGES.length - 1);

  return (
    <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white">
      <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:p-6">
        <div className="min-w-0 flex-1">
          <Eyebrow className="mb-2">
            Setup · stage {current + 1} of {SETUP_STAGES.length} · {def.label}
          </Eyebrow>

          <p className="font-display text-lg font-bold tracking-[-0.03em] text-primary">
            {next ? next.label : def.headline}
          </p>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
            {next?.owner === 'you' ? 'Waiting on you' : 'We are on it'}
          </p>

          {/* The rail, without the labels — those live on /welcome. */}
          <div className="relative mt-4 max-w-sm">
            <div className="absolute inset-x-0 top-1 h-px bg-zinc-200" aria-hidden />
            <motion.div
              className="absolute left-0 top-1 h-px bg-primary"
              initial={false}
              animate={{ width: `${fill * 100}%` }}
              transition={{ type: 'spring', stiffness: 60, damping: 18 }}
              aria-hidden
            />
            <ol className="relative flex items-center justify-between">
              {SETUP_STAGES.map((id, index) => (
                <li
                  key={id}
                  title={STAGE_DEFS[id].label}
                  className={
                    index <= current
                      ? 'h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-white'
                      : 'h-2.5 w-2.5 rounded-full bg-zinc-200 ring-2 ring-white'
                  }
                />
              ))}
            </ol>
          </div>
        </div>

        <Link
          to={next?.href ?? '/welcome'}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
        >
          {next?.owner === 'you' ? 'Finish setup' : 'See progress'}
          <span aria-hidden>→</span>
        </Link>
      </div>
    </div>
  );
}
